import { cn, Skeleton } from "@ondit/ui";
import type { ReactNode } from "react";
import { useT } from "~/core/i18n";

/*
 * Stand-ins shaped like what is about to arrive. The spinning seal is for a
 * page that has nothing on it yet; inside a frame that has already drawn, a
 * skeleton the shape of the list says what is coming, and the page does not
 * jump when it lands.
 */

const ROWS = [0, 1, 2, 3, 4, 5];

function Loading({ className, children }: { className?: string; children: ReactNode }) {
	const t = useT();
	return (
		<div className={className} role="status" aria-label={t("state.loading")}>
			{children}
		</div>
	);
}

/** A table under its toolbar: search on the left, an action on the right. */
export function TableSkeleton({ className }: { className?: string }) {
	return (
		<Loading className={cn("flex flex-col gap-(--s-6)", className)}>
			<div className="flex items-center justify-between gap-(--s-6)">
				<Skeleton className="h-(--control-h) w-60 max-w-full" />
				<Skeleton className="h-(--control-h) w-24" />
			</div>
			{ROWS.map((row) => (
				<div
					key={row}
					className="grid grid-cols-[2fr_1fr_1fr_1fr] items-center gap-(--s-8) border-(--border) border-b py-(--s-6)"
				>
					<Skeleton className="h-(--s-7) w-3/4" />
					<Skeleton className="h-(--s-7) w-1/2" />
					<Skeleton className="h-(--s-7) w-2/3" />
					<Skeleton className="h-(--s-7) w-1/3" />
				</div>
			))}
		</Loading>
	);
}

/** A searchable list of rows, each an icon beside a name and one line about it. */
export function ListSkeleton({ className }: { className?: string }) {
	return (
		<Loading className={cn("flex flex-col gap-(--s-8)", className)}>
			<Skeleton className="h-(--control-h) w-full" />
			{ROWS.map((row) => (
				<div key={row} className="flex items-center gap-(--s-6)">
					<Skeleton className="size-(--control-h) shrink-0" />
					<div className="flex flex-1 flex-col gap-(--s-3)">
						<Skeleton className="h-(--s-7) w-1/3" />
						<Skeleton className="h-(--s-6) w-2/3" />
					</div>
				</div>
			))}
		</Loading>
	);
}

/** A heading over a composer, with the suggestions under it. */
export function ComposerSkeleton({ className }: { className?: string }) {
	return (
		<Loading
			className={cn(
				"mx-auto flex w-full max-w-(--prose-width) flex-col items-center gap-(--s-8) px-(--s-10) pt-(--s-13)",
				className,
			)}
		>
			<Skeleton className="h-(--s-11) w-1/2" />
			<Skeleton className="h-[calc(var(--control-h)*3)] w-full rounded-(--radius-lg)" />
			<div className="flex gap-(--s-6)">
				{ROWS.slice(0, 3).map((chip) => (
					<Skeleton key={chip} className="h-(--control-h-sm) w-28" />
				))}
			</div>
		</Loading>
	);
}

/** A surface that is one drawing area, like the workflow canvas. */
export function CanvasSkeleton({ className }: { className?: string }) {
	return (
		<Loading className={cn("h-full p-(--s-10)", className)}>
			<Skeleton className="h-full" />
		</Loading>
	);
}
